import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { useLeads, useTeam } from "../../hooks/useFirebase";

const LeadsPage = () => {
  const { leads, loading, addLead, updateLead, deleteLead } = useLeads();
  const { teamMembers } = useTeam();
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", source: "", assignedTo: "",
    status: "new", nextFollowUp: "", notes: ""
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.phone) { alert("Name & Phone required!"); return; }
    try {
      await addLead({ ...formData, createdBy: "admin" });
      setShowModal(false);
      setFormData({ name: "", phone: "", email: "", source: "", assignedTo: "", status: "new", nextFollowUp: "", notes: "" });
    } catch (e) { alert("Failed to add lead."); }
  };

  const handleStatusChange = async (id, status) => {
    try { await updateLead(id, { status }); } catch (e) { alert("Failed!"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this lead?")) {
      try { await deleteLead(id); } catch (e) { alert("Failed!"); }
    }
  };

  const filtered = leads.filter((l) => filterStatus === "all" || l.status === filterStatus);

  const columns = [
    {
      header: "Lead Info",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="admin-avatar small">
            <span>{row.name?.split(" ").map((n) => n[0]).join("").substring(0, 2)}</span>
          </div>
          <div>
            <div className="font-bold text-14 text-gray-900">{row.name}</div>
            <div className="text-12 text-gray-400">{row.phone}</div>
          </div>
        </div>
      )
    },
    {
      header: "Status",
      render: (row) => (
        <select
          className={`status-select select-${row.status}`}
          value={row.status}
          onChange={(e) => handleStatusChange(row.id, e.target.value)}
        >
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="interested">Interested</option>
          <option value="callback">Callback</option>
          <option value="not_interested">Not Interested</option>
          <option value="converted">Converted</option>
        </select>
      )
    },
    {
      header: "Source",
      render: (row) => (
        <span className="status-badge badge-source">{row.source || "-"}</span>
      )
    },
    {
      header: "Next Follow-up",
      render: (row) => (
        <span className={`text-12 font-semibold ${row.nextFollowUp ? "text-red-600" : "text-gray-400"}`}>
          {row.nextFollowUp || "—"}
        </span>
      )
    },
    {
      header: "Created",
      render: (row) => (
        <span className="text-12 text-gray-400">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-IN") : "-"}
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-wrap">
          <div className="spinner" />
          <p className="spinner-text">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Leads Management" breadcrumbs={["Admin", "Leads"]} />

      {/* Stats */}
      <div className="admin-grid admin-grid-4" style={{ marginTop: 24, marginBottom: 24 }}>
        {[
          { label: "Total Leads", value: leads.length, color: "#3b82f6" },
          { label: "New", value: leads.filter((l) => l.status === "new").length, color: "#2563eb" },
          { label: "Interested", value: leads.filter((l) => l.status === "interested").length, color: "#22c55e" },
          { label: "Converted", value: leads.filter((l) => l.status === "converted").length, color: "#a855f7" },
        ].map((s, i) => (
          <div key={i} className="quick-stat qs-blue">
            <div className="quick-stat-icon" style={{ backgroundColor: s.color, color: "white" }}>{s.value}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar" style={{ marginBottom: 24 }}>
        <div className="toolbar-left">
          <select
            className="admin-form-group-input"
            style={{ padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status ({leads.length})</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="interested">Interested</option>
            <option value="callback">Callback</option>
            <option value="converted">Converted</option>
          </select>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add New Lead
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filtered}
        onRowAction={(row, action) => {
          if (action === "delete") handleDelete(row.id);
        }}
      />

      {/* Add Lead Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Add New Lead</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter lead name" />
                </div>
                <div className="admin-form-group">
                  <label>Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 9876543210" />
                </div>
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" />
                </div>
                <div className="admin-form-group">
                  <label>Source *</label>
                  <select name="source" value={formData.source} onChange={handleChange}>
                    <option value="">Select Source</option>
                    <option>Website</option>
                    <option>Google Ads</option>
                    <option>Referral</option>
                    <option>Facebook</option>
                    <option>LinkedIn</option>
                    <option>Walk-in</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Assign To</label>
                  <select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                    <option value="">Select Team Member</option>
                    {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Next Follow-up Date</label>
                  <input type="date" name="nextFollowUp" value={formData.nextFollowUp} onChange={handleChange} />
                </div>
              </div>
              <div className="admin-form-group" style={{ marginTop: 16 }}>
                <label>Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Any additional info..." rows="3" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd}>💾 Save to Firebase</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;